import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';

const emptyForm = () => ({
  code: '',
  label: '',
  sort_order: 0,
  is_active: true
});

const WorkFunctionManager = ({ hideHeader = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get('/api/admin/work-functions');
      const data = res?.data?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load work functions:', err);
      setError(err.response?.data?.message || 'Failed to load work functions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
    setSaving(false);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      code: row.code || '',
      label: row.label || '',
      sort_order: row.sort_order ?? 0,
      is_active: row.is_active ?? true
    });
    setDialogOpen(true);
    setSaving(false);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setSaving(false);
  };

  const toggleActive = async (row) => {
    try {
      setError('');
      await axios.put(`/api/admin/work-functions/${row.id}`, {
        code: row.code,
        label: row.label,
        sort_order: row.sort_order,
        is_active: !row.is_active
      });
      await load();
    } catch (err) {
      console.error('Failed to toggle work function:', err);
      setError(err.response?.data?.message || 'Failed to update work function');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setError('');
      await axios.delete(`/api/admin/work-functions/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      console.error('Failed to delete work function:', err);
      setError(err.response?.data?.message || 'Failed to delete work function');
      setDeleteTarget(null);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (editingId) {
        await axios.put(`/api/admin/work-functions/${editingId}`, form);
      } else {
        await axios.post('/api/admin/work-functions', form);
      }

      await load();
      closeDialog();
    } catch (err) {
      console.error('Failed to save work function:', err);
      setError(err.response?.data?.message || 'Failed to save work function');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {!hideHeader && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" component="h2" sx={{ fontSize: '1.15rem' }}>
            Function Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the task types shown in Time Entries.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>
          Add Function
        </Button>
      </Box>

      {error && (
        <AlertBox severity="error" message={error} />
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Sort</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.code}</TableCell>
                <TableCell>{r.label}</TableCell>
                <TableCell>{r.sort_order}</TableCell>
                <TableCell>
                  <Chip
                    label={r.is_active ? 'Active' : 'Inactive'}
                    color={r.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(r)} title="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => toggleActive(r)}
                    title={r.is_active ? 'Disable' : 'Enable'}
                    color={r.is_active ? 'default' : 'success'}
                  >
                    {r.is_active ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleteTarget(r)} title="Delete" color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No functions configured.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Function</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteTarget?.label}</strong> ({deleteTarget?.code})?
            This cannot be undone. If this function is used by existing time entries, deletion will be blocked — set it to Inactive instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Function' : 'Add Function'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              label="Code"
              size="small"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              helperText="Unique identifier (e.g. weld, laser)"
            />
            <TextField
              label="Label"
              size="small"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
            <TextField
              label="Sort order"
              size="small"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
            />
            <TextField
              label="Status"
              size="small"
              select
              value={form.is_active ? 'true' : 'false'}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === 'true' }))}
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const AlertBox = ({ severity, message }) => (
  <Box sx={{ mb: 1 }}>
    <Box
      sx={{
        p: 1,
        border: '1px solid',
        borderColor: severity === 'error' ? 'error.main' : 'warning.main',
        borderRadius: 1,
        color: severity === 'error' ? 'error.main' : 'warning.main',
        backgroundColor: 'background.paper'
      }}
    >
      <Typography variant="body2">{message}</Typography>
    </Box>
  </Box>
);

export default WorkFunctionManager;

